import React, { useState, useMemo, useEffect, useRef } from "react";
import ErrorMessage from "../../../components/FormComponents/ErrorMessage";
import Button from "../../../components/FormComponents/Button";
import Input from "../../../components/FormComponents/Input";
import SingleSelectBox from "../../../components/FormComponents/SingleSelectBox";
import axiosInstance, { handleApiError } from "../../../services/axiosService";
import { debounce } from "../../../commonFunctions/arrayFunc";
import ChipList from "../../../components/BuilderComponents/ChipList";
import useClickOutside from "../../../hooks/useClickOutside";

const TYPE_OPTIONS = [
  { label: "Specific Users", value: "USER" },
  { label: "Anyone", value: "ANYONE" }
];

const UserSearch = ({ label, selectedUsers = [], onChange, placeholder }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef();

  useClickOutside(dropdownRef, () => setIsOpen(false));

  const fetchSearchResults = useMemo(() => {
    return debounce(async (searchTerm) => {
      try {
        setIsLoading(true);
        const url = searchTerm ? `/users?search=${searchTerm}&limit=20` : `/users?limit=10`;
        const { data: responseData } = await axiosInstance.get(url);

        const results = responseData?.users || [];
        setSearchResults(
          results.filter((user) => !selectedUsers.some((assignedUser) => assignedUser.email === user.email))
        );
      } catch (error) {
        handleApiError(error);
        setSearchResults([]);
      } finally {
        setIsLoading(false);
      }
    }, 300);
  }, [selectedUsers]);

  useEffect(() => {
    if (isOpen && searchTerm === "") {
      fetchSearchResults("");
    }
  }, [isOpen, searchTerm, fetchSearchResults]);
  const handleSelect = (user) => {
    const fullName = user.name || (user.first_name ? `${user.first_name} ${user.last_name}`.trim() : user.email);
    const newUser = {
      email: user.email,
      name: fullName,
      id: user.id
    };
    onChange([...selectedUsers, newUser]);
    setSearchTerm("");
    setIsOpen(false);
  };

  const handleRemove = (userToRemove) => {
    onChange(selectedUsers.filter((u) => u.email !== userToRemove.email));
  };

  return (
    <div className="d_flex flex_column gap_2" ref={dropdownRef}>
      <div className="position_relative">
        <Input
          label={label}
          placeholder={placeholder}
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setIsOpen(true);
            fetchSearchResults(e.target.value);
          }}
          onFocus={() => setIsOpen(true)}
        />
        {isOpen && (
          <div
            className="position_absolute w_100 bg_light border shadow_sm z_index_10 overflow_auto border_radius_small"
            style={{ top: "100%", maxHeight: "200px" }}
          >
            {isLoading ? (
              <div className="p_3 text_center color_secondary font_size_small">Loading users...</div>
            ) : searchResults.length > 0 ? (
              searchResults.map((user) => (
                <div
                  key={user.id || user.email}
                  className="p_2 hover_bg_gray cursor_pointer d_flex flex_column border_bottom"
                  onClick={() => handleSelect(user)}
                >
                  <span className="font_size_xsmall font_weight_medium">
                    {user.name || (user.first_name ? `${user.first_name} ${user.last_name}`.trim() : "")}
                  </span>
                  <span className="font_size_xsmall color_secondary">{user?.email || ""}</span>
                </div>
              ))
            ) : (
              <div className="p_3 text_center color_secondary font_size_small">No users found</div>
            )}
          </div>
        )}
      </div>

      <div className="mt_1">
        <ChipList chips={selectedUsers} labelKey={["email"]} removeChip={(chip) => handleRemove(chip)} />
      </div>
    </div>
  );
};

const InitiatorForm = ({ formData, handleChange }) => {
  return (
    <div className="d_flex flex_column gap_2">
      <div className="d_flex flex_column gap_1">
        <SingleSelectBox
          label="Type"
          placeholder="Select initiator type"
          options={TYPE_OPTIONS}
          labelKey="label"
          valueKey="value"
          selectedData={formData.initiator_type}
          onChange={(option) => handleChange("initiator_type", option?.value || "")}
          containerClassName="w_100"
        />
      </div>

      {formData.initiator_type === "USER" && (
        <UserSearch
          label="Select Initiators"
          selectedUsers={formData.initiators}
          onChange={(val) => handleChange("initiators", val)}
          placeholder="Search and select users..."
        />
      )}
    </div>
  );
};

const ApproversForm = ({ formData, handleChange }) => {
  return (
    <div className="d_flex flex_column gap_2">
      <div className="d_flex flex_column gap_1">
        <SingleSelectBox
          label="Type"
          placeholder="Select approver type"
          options={TYPE_OPTIONS}
          labelKey="label"
          valueKey="value"
          selectedData={formData.approver_type}
          onChange={(option) => handleChange("approver_type", option?.value || "")}
          containerClassName="w_100"
        />
      </div>

      {formData.approver_type === "USER" && (
        <UserSearch
          label="Select Approvers"
          selectedUsers={formData.approvers}
          onChange={(val) => handleChange("approvers", val)}
          placeholder="Search and select users..."
        />
      )}

      <div className="d_flex flex_column gap_1">
        <Input
          type="number"
          label="Minimum Approvers Required"
          min="0"
          value={formData.min_approvers}
          onChange={(e) => handleChange("min_approvers", parseInt(e.target.value, 10) || 0)}
          containerClassName="w_100"
        />
      </div>
    </div>
  );
};

const PipelineConfigSettings = ({ config, onClose, onSubmit }) => {
  const [formData, setFormData] = useState(() => ({
    id: config?.id ?? null,
    min_approvers: config?.min_approvers ?? 0,
    initiator_type: config?.initiator_type ?? "",
    initiators: config?.initiators ?? [],
    approver_type: config?.approver_type ?? "",
    approvers: config?.approvers ?? [],
    enable_email: config?.enable_email ?? false
  }));

  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (key, value) => {
    setFormData((prev) => {
      const newState = { ...prev, [key]: value };

      if (key === "initiator_type" && value === "ANYONE") {
        newState.initiators = [];
      }
      if (key === "approver_type" && value === "ANYONE") {
        newState.approvers = [];
        newState.min_approvers = 1;
      }

      return newState;
    });
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);

    try {
      const payload = {
        ...formData,
        initiators: formData.initiators.map((u) => ({
          email: u.email,
          name: u.first_name || u.name || "",
          action: ""
        })),
        approvers: formData.approvers.map((u) => ({
          email: u.email,
          name: u.first_name || u.name || "",
          action: ""
        }))
      };

      await onSubmit(payload);
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to update configuration");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="d_flex flex_column h_100">
      <div className="flex_grow overflow_auto p_3 d_flex flex_column gap_4">
        <InitiatorForm formData={formData} handleChange={handleChange} />
        <div className="border_bottom"></div>
        <ApproversForm formData={formData} handleChange={handleChange} />
        <div className="border_bottom"></div>

        <div>
          <Input
            type="checkbox"
            label="Enable Email Notifications"
            checked={formData.enable_email}
            onChange={(e) => handleChange("enable_email", e.target.checked)}
            inputClassName="cursor_pointer"
            containerClassName="mb_0"
          />
        </div>
        {error && <ErrorMessage>{error}</ErrorMessage>}
      </div>

      <div className="p_3 border_top d_flex justify_end gap_2 bg_gray">
        <Button onClick={() => onClose(false)} disabled={isSaving} variant="secondary">
          Cancel
        </Button>

        <Button onClick={handleSave} isLoading={isSaving} variant="primary">
          Save Changes
        </Button>
      </div>
    </div>
  );
};

export default PipelineConfigSettings;
